import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "../../stories/Buttons/Button";
import { Alert, Modal, Spinner } from "react-bootstrap";
import { useNavigate, useLocation, Link } from "react-router-dom";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import exclamationIcon from "../../assets/icons/exclamation_icon.svg";
import shield from "../../assets/icons/shield.svg";
import scope from "../../assets/icons/scope.svg";
import { getTestTypeValueFromUrl, TEST_TYPE_TO_NAME } from "../../utils";
import CancelCurrentAppointment from "./CancelCurrentAppointment";
import { useMediaQuery } from "react-responsive";

let resume_link;

/**
 * Component that handles the continuation of a user's registration journey.
 * Displays modal for rejecting a booking and provides links for resuming or canceling registration.
 *
 */

const ContinueJourney = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const { callApi, resultPost } = useApis();

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showCancelComponent, setShowCancelComponent] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 840 });

  // Set the correct resume link based on the current registration stage
  switch (props.resume?.registration_stage) {
    case "type-confirm":
      resume_link = `/bloods/boots/${props.test_type}/booking/confirm-details/${props.resume?.id}/`;
      break;
    case "consent":
      resume_link = `/bloods/boots/${props.test_type}/booking/confirm-details/${props.resume?.id}/`;
      break;
    case "personal-details":
      resume_link = `/bloods/boots/assessment/${props.test_type}/${props.resume?.id}/1`;
      break;
    case "assessment":
      resume_link = `/bloods/boots/assessment-form/${props.test_type}/${props.resume.id}/`;
      break;
    default:
      break;
  }

  // API call to reject the booking
  const rejectBooking = async () => {
    try {
      // Await the response from the API
      await callApi(
        {},
        `/api/dashboard/reject-booking/${props.resume?.id}/`,
        "POST",
      );
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error rejecting booking", error);
    }
  };

  // useEffect to show toast after API call response
  useEffect(() => {
    if (resultPost) {
      if (resultPost.status === "rejected") {
        props.isBookingRejected();
        auth?.showToast(
          "Booking Cancelled",
          "Your appointment has been cancelled  successfully. You can proceed to book a new one now.",
          "../assets/mhc.png",
          <faX />,
        );
        {
          setShowCancelComponent
            ? navigate(
                `/bloods/boots/${location.pathname
                  .split("/boots/")[1]
                  ?.split("/")[0] || localStorage.getItem("test_type")}`,
              )
            : navigate(
                `/bloods/boots/${props.resume?.test_type ||
                  sessionStorage.getItem("test_type")}`,
              );
        }
      } else {
        auth?.showToast(
          "Reject Booking",
          "Rejected failed",
          "../assets/mhc.png",
          <faX />,
        );
      }
    }
  }, [resultPost]);

  // Toggle modal visibility
  const toggleModal = () => setModalOpen(!modalOpen);

  // Handle modal confirmation to reject the booking
  const handleRejectBooking = () => {
    toggleModal();
  };

  // Reject an appointment
  const handleConfirmReject = () => {
    rejectBooking();
    toggleModal();
  };

  useEffect(() => {
    const resumeTestType = props.resume?.test_type;
    const rawUrlTestType = getTestTypeValueFromUrl(location.pathname.split("/boots/")[1]?.split("/")[0]);

    if (!resumeTestType || !rawUrlTestType) {
      setShowCancelComponent(false);
      return;
    }

    if (rawUrlTestType === "unknown") {
      const segments = location.pathname.split("/");
      const bootsIndex = segments.indexOf("boots");

      if (bootsIndex !== -1) {
        segments[bootsIndex + 1] = resumeTestType;
      }

      const correctedPath = segments.join("/");
      navigate(correctedPath, { replace: true });
      return;
    }

    // Compare raw URL test_type directly with resumeTestType
    if (rawUrlTestType !== resumeTestType) {
      setShowCancelComponent(true);
      return;
    }

    setShowCancelComponent(false);
  }, [location.pathname, props.resume?.test_type, navigate]);

  if (isLoading) {
    return (
      <div className="text-center">
        <Spinner
          animation="border"
          role="output"
          className="my-custom-spinner"
        />
      </div>
    );
  }

  if (showCancelComponent) {
    return (
      <CancelCurrentAppointment
        handleRejectBooking={handleRejectBooking}
        handleConfirmReject={handleConfirmReject}
        toggleModal={toggleModal}
        modalOpen={modalOpen}
        test_type={location.pathname.split("/boots/")[1]?.split("/")[0]}
        resumeTestType = {props.resume?.test_type}
      />
    );
  }

  return (
    <div className="row justify-content-center dashboard-page registration-continuous-journey">
      <div className="col-md-12">
        <div className="dashboard">
          <div className="d-flex align-items-center">
            <h1 className="mb-0">Open Registered Appointments</h1>
          </div>

          {props.untestable && <></>}
          {props.resume && (
            
            <div className="resume-registration">
              <Alert className="mt-3 custom-width-for-assessment row ms-0" variant="dark" fade={false}>
                <span className="col-md-8">
                  <button className="btn-required ageo-bold"><span className="dot"></span><span className="pt-1"> Action required</span></button><br/>
                  <strong>
                    <Link className="alert-link">
                      Register your Boots store appointment
                    </Link>
                  </strong>
                  <p className="text-muted ageo-medium">Your registration with Boots In-store Wellness Testing Service powered by MyHealthChecked is not yet complete. Please click the button below to continue your registration.</p>
                </span>
                <div className={`col-md-4 d-flex justify-content-center align-items-end ${isMobile ? "flex-column justify-content-center gap-3" : "justify-content-end flex-column"}`}>
                  <Button
                    className={`btn-green ${isMobile ? "w-100 me-0 mb-0" : "mb-3"}`}
                    href={resume_link}
                    color="primary"
                    label="Continue Registration"
                  />
                  <Button
                    onClick={handleRejectBooking}
                    variant="dark"
                    className={`pt-2 btn-white justify-content-end ${isMobile && "w-100"}`}
                    size="sm"
                    label="Cancel Registration"
                  />
                </div>
              </Alert>
              {/* <h4 className="fs-20 ageo-medium text-teal my-0">
                Your registration with Boots In-store Wellness Testing Service
                powered by MyHealthChecked is not yet complete.
              </h4>
              <p className="fs-20 ageo-medium text-teal">
                Please click the button below to continue your registration.
              </p>
              <p className="ageo-regular text-muted mt-4">You’re booked for: <b className="text-teal">{TEST_TYPE_TO_NAME[props.resume?.test_type]}</b></p>
              <div className="registration-button-wrapper">
                <Button
                  color="primary"
                  href={resume_link}
                  label="Continue registration"
                />

                <Button
                  className="btn-white mx-3"
                  label="Cancel registration"
                  onClick={handleRejectBooking}
                  variant="outline"
                />
              </div> */}
              <Alert className="mt-4 text-justify rounded" variant="dark">
                <strong className="alert-heading text-teal">
                  IMPORTANT REMINDERS{" "}
                </strong>
                <div className="row text-teal mt-2">
                  <div className="col-md-6 d-flex first-paragraph">
                    <img
                      src={shield}
                      alt="Shield Icon"
                      className="alert-icon mb-2 me-2"
                    />
                    <p className="fs-20">
                      <strong className="ageo-bold">One test limit:</strong> You
                      can only register for one test at a time. Once you've
                      received your test results, you'll be able to register for
                      another.
                    </p>
                  </div>
                  <div className="col-md-6 d-flex">
                    <img
                      src={scope}
                      alt="Scope Icon"
                      className="alert-icon mb-2 me-2"
                    />
                    <p className="fs-20 mb-0">
                      <strong className="ageo-bold">Pre-arrival:</strong> It's
                      essential to complete your registration before arriving at
                      Boots for your in-store appointment. If your registration
                      is incomplete, you may be unable to proceed with your
                      test.
                    </p>
                  </div>
                </div>
              </Alert>
              <Modal
                className="confirm-rejection-modal"
                show={modalOpen}
                onHide={toggleModal}
              >
                <Modal.Header closeButton>
                  <Modal.Title>Confirm Cancellation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  Are you sure you want to cancel this appointment? This action
                  cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    className="btn-gray"
                    variant={"secondary"}
                    onClick={toggleModal}
                    label="Cancel"
                  />
                  <Button
                    className="btn-dark"
                    onClick={handleConfirmReject}
                    label="Confirm"
                  />
                </Modal.Footer>
              </Modal>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
ContinueJourney.propTypes = {
  resume: PropTypes.shape({
    registration_stage: PropTypes.string,
    id: PropTypes.string,
    test_type: PropTypes.string,
  }),
  test_type: PropTypes.string,
  untestable: PropTypes.bool,
  isBookingRejected: PropTypes.func,
};

export default ContinueJourney;
